import React, { createContext, useContext, useState, useRef, useCallback } from "react";
import { ReconFilterFormData } from "./ReconCommon.types";
import { GroupBreakdownResponse } from "../../../Types/Reconautomation.types";
import { ViewType } from "./ReconFilterActionsPanel";

export const INITIAL_FILTERS: ReconFilterFormData = {
  source: "Britannia",
  start_date: "",
  end_date: "",
  contract: "",
  buy_sell: "",
};

interface CachedRunResult<TSummary = unknown, TResults = unknown> {
  summary: TSummary;
  results: TResults;
  runId: string | null;
  fileName: string | null;
  breakdownByGroupId?: Record<string, GroupBreakdownResponse>;
}

interface ReconSharedState {
  filters: ReconFilterFormData;
  updateFilter: (key: keyof ReconFilterFormData, value: string) => void;

  selectedFile: File | null;
  setSelectedFile: (f: File | null) => void;

  lastFileName: string | null;
  setLastFileName: (n: string | null) => void;

  fileInputRef: React.RefObject<HTMLInputElement>;

  resetAll: () => void;

  autoRunOnMount: boolean;
  setAutoRunOnMount: (v: boolean) => void;

  version1Cache: CachedRunResult | null;
  setVersion1Cache: (data: CachedRunResult | null) => void;

  version2Cache: CachedRunResult | null;
  setVersion2Cache: (data: CachedRunResult | null) => void;

  viewType: ViewType;
  setViewType: (v: ViewType) => void;
}

const ReconSharedContext = createContext<ReconSharedState | null>(null);

export const ReconSharedProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [filters, setFilters] = useState<ReconFilterFormData>(INITIAL_FILTERS);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [lastFileName, setLastFileName] = useState<string | null>(null);
  const [autoRunOnMount, setAutoRunOnMount] = useState(false);

  const [viewType, setViewTypeState] = useState<ViewType>(() => {
    const stored = localStorage.getItem("recon_view_type");
    return stored === "version-1" || stored === "version-2" ? stored : "version-2";
  });

  const setViewType = useCallback((v: ViewType) => {
    setViewTypeState(v);
    localStorage.setItem("recon_view_type", v);
  }, []);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [version1Cache, setVersion1Cache] = useState<CachedRunResult | null>(null);
  const [version2Cache, setVersion2Cache] = useState<CachedRunResult | null>(null);

  const updateFilter = useCallback((key: keyof ReconFilterFormData, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setVersion1Cache(null);
    setVersion2Cache(null);
  }, []);

  const handleSetSelectedFile = useCallback((f: File | null) => {
    setSelectedFile(f);
    setVersion1Cache(null);
    setVersion2Cache(null);
  }, []);

  const resetAll = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setSelectedFile(null);
    setLastFileName(null);
    setAutoRunOnMount(false);
    setVersion1Cache(null);
    setVersion2Cache(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  return (
    <ReconSharedContext.Provider
      value={{ filters, updateFilter, selectedFile, setSelectedFile: handleSetSelectedFile, lastFileName, setLastFileName, fileInputRef, resetAll, autoRunOnMount, setAutoRunOnMount, version1Cache, setVersion1Cache, version2Cache, setVersion2Cache, viewType, setViewType, }}
    >
      {children}
    </ReconSharedContext.Provider>
  );
};

export const useReconShared = () => {
  const ctx = useContext(ReconSharedContext);
  if (!ctx) throw new Error("useReconShared must be used inside <ReconSharedProvider>");
  return ctx;
};